/**
 * The admin nav model — GROUPED.
 *
 * Admin used to be 13 sibling pill tabs in one row, which put "User Management"
 * (the thing an admin opens almost every time) fifth in a scrolling strip of ops
 * tooling. Now there are three groups and the day job is the landing page:
 *
 *   Access  →  Users · Pending requests          ← /admin lands HERE
 *   System  →  Overview · Ops · Status · System Info · Cron · LLM Budget · Flags
 *   Tools   →  KB Ingest · Grade · Session View · Improvements
 *
 * Nothing was removed — every page still exists at its original path, so old
 * links and bookmarks keep working. The second row (the group's own items) only
 * renders when the group has more than one page.
 *
 * Paths are absolute under `/admin` (the feature owns the whole subtree per
 * src/features/README.md).
 */
export type AdminNavItem = { path: string; label: string; end?: boolean };

export type AdminNavGroup = {
  key: string;
  label: string;
  /** Where the group tab navigates — its first page. */
  path: string;
  items: AdminNavItem[];
};

export const ADMIN_GROUPS: AdminNavGroup[] = [
  {
    key: 'access',
    label: 'Access',
    path: '/admin/users',
    items: [
      // `end` so the Users tab isn't left active while a nested route is open.
      { path: '/admin/users', label: 'Users', end: true },
      { path: '/admin/access-requests', label: 'Pending requests' },
    ],
  },
  {
    key: 'system',
    label: 'System',
    path: '/admin/command-center',
    items: [
      { path: '/admin/command-center', label: 'Overview' },
      { path: '/admin/ops-dashboard', label: 'Ops' },
      { path: '/admin/status', label: 'Status' },
      { path: '/admin/system-info', label: 'System Info' },
      { path: '/admin/cron-audit', label: 'Cron' },
      { path: '/admin/llm-budget', label: 'LLM Budget' },
      { path: '/admin/feature-flags', label: 'Feature Flags' },
    ],
  },
  {
    key: 'tools',
    label: 'Tools',
    path: '/admin/kb-ingest',
    items: [
      { path: '/admin/kb-ingest', label: 'KB Ingest' },
      { path: '/admin/grade', label: 'Grade' },
      { path: '/admin/session-view', label: 'Session View' },
      { path: '/admin/improvements', label: 'Improvements' },
    ],
  },
];

/**
 * Flat list of every admin destination, derived from the groups. Kept because the
 * Command Center renders a link grid over it — and because deriving it means a
 * page can never be in the grid but not the nav (or the reverse).
 */
export const ADMIN_NAV: AdminNavItem[] = ADMIN_GROUPS.flatMap((g) => g.items);

/** The group that owns a given pathname, for highlighting the top row. */
export function groupForPath(pathname: string): AdminNavGroup | undefined {
  // Longest matching item path wins, so /admin/users and /admin/users/x agree.
  let best: { group: AdminNavGroup; len: number } | undefined;
  for (const group of ADMIN_GROUPS) {
    for (const item of group.items) {
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        if (best === undefined || item.path.length > best.len) {
          best = { group, len: item.path.length };
        }
      }
    }
  }
  // `/admin` itself is the Access group (it redirects to /admin/users).
  if (best === undefined && (pathname === '/admin' || pathname === '/admin/')) {
    return ADMIN_GROUPS[0];
  }
  return best?.group;
}
